import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { axiosInstance } from '../../services/axiosInstance';
import { formatApiErrorMessage } from '../../utils/apiErrorMessage';
import { formatDateTimeKst } from '../../utils/dateUtils';

type PoolStreamRow = {
  pool_id: number;
  code: string;
  name: string;
  webrtc_ready: boolean;
  central_active_count: number;
  central_slot_count: number;
  agent_stale: boolean;
  agent_age_sec: number | null;
  whip_ts_buffer_bytes: number | null;
  whip_forwarding: boolean | null;
  whip_base_set: boolean | null;
  whip_base_host: string;
  edge_pipeline: string;
  gateway_nvr_bypass: boolean;
  gateway_stream_width: number | null;
  agent: { reported_at?: string } | null;
};

type StreamStatusBody = {
  central_go2rtc_available: boolean;
  updated_at?: string;
  ui_guide?: string;
  pools: PoolStreamRow[];
};

function formatTsBuffer(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return '—';
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(bytes % 1048576 === 0 ? 0 : 1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function formatAge(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return '—';
  if (sec < 60) return `${Math.round(sec)}초 전`;
  if (sec < 3600) return `${Math.round(sec / 60)}분 전`;
  return `${Math.round(sec / 3600)}시간 전`;
}

/**
 * brand-manager: 시설별 WHIP ts_buffer · WebRTC 수신 · 관제 해상도/버퍼 안내 (폴링).
 */
const BrandManagerGatewayStreamMonitorSection: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [body, setBody] = useState<StreamStatusBody | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axiosInstance.get('/api/brand-menu/gateway-stream-status');
      setBody({
        central_go2rtc_available: Boolean(data?.central_go2rtc_available),
        updated_at: data?.updated_at,
        ui_guide: typeof data?.ui_guide === 'string' ? data.ui_guide : undefined,
        pools: Array.isArray(data?.pools) ? data.pools : [],
      });
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '게이트웨이 스트림 상태 조회 실패'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void load();
    const id = window.setInterval(() => void load(), 15000);
    return () => window.clearInterval(id);
  }, [open, load]);

  const pools = body?.pools || [];

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>
          시설 WebRTC · WHIP 버퍼 (실시간)
        </Typography>
        <Button size="small" onClick={() => void load()} disabled={loading || !open}>
          새로고침
        </Button>
        {loading ? <CircularProgress size={16} /> : null}
        <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label="펼치기">
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5, mb: open ? 1 : 0 }}>
        시설 상태「활성」만 표시. 현장 DATA_GATEWAY_WHIP_TS_BUFFER · 관제 해상도/버퍼는 HLS만
        적용(WebRTC는 원본). 15초마다 갱신.
      </Typography>
      <Collapse in={open}>
        {error ? (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}
        {body?.ui_guide ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {body.ui_guide}
          </Alert>
        ) : null}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Chip
            size="small"
            color={body?.central_go2rtc_available ? 'success' : 'warning'}
            label={body?.central_go2rtc_available ? '중앙 go2rtc 기동' : '중앙 go2rtc 미기동'}
          />
          {body?.updated_at ? (
            <Chip
              size="small"
              variant="outlined"
              label={`조회 ${formatDateTimeKst(body.updated_at)}`}
            />
          ) : null}
        </Box>
        {pools.length === 0 && !loading && !error ? (
          <Typography variant="body2" color="text.secondary">
            활성 시설 없음
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>시설</TableCell>
                  <TableCell>WebRTC</TableCell>
                  <TableCell>활성/슬롯</TableCell>
                  <TableCell>WHIP ts_buffer</TableCell>
                  <TableCell>WHIP 전송</TableCell>
                  <TableCell>파이프</TableCell>
                  <TableCell>원본 전송</TableCell>
                  <TableCell>재인코딩 폭</TableCell>
                  <TableCell>보고</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pools.map((row) => (
                  <TableRow key={row.pool_id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.name || row.code || `#${row.pool_id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.code} · id {row.pool_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={row.webrtc_ready ? 'success' : 'default'}
                        label={row.webrtc_ready ? '수신' : '없음'}
                      />
                    </TableCell>
                    <TableCell>
                      {row.central_active_count}/{row.central_slot_count}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      {formatTsBuffer(row.whip_ts_buffer_bytes)}
                      {row.whip_base_host ? (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.whip_base_set ? row.whip_base_host : 'WHIP 주소 없음'}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {row.whip_forwarding == null
                        ? '—'
                        : row.whip_forwarding
                          ? '동작'
                          : '없음'}
                    </TableCell>
                    <TableCell>{row.edge_pipeline || '—'}</TableCell>
                    <TableCell>
                      {row.gateway_nvr_bypass ? (
                        <>
                          <Typography variant="body2">ON (copy)</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            재인코딩 없음
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2">OFF</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            게이트웨이 재인코딩
                          </Typography>
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.gateway_nvr_bypass ? (
                        <>
                          <Typography variant="body2">미적용</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            저장값{' '}
                            {row.gateway_stream_width != null
                              ? `${row.gateway_stream_width}p`
                              : '—'}
                          </Typography>
                        </>
                      ) : row.gateway_stream_width != null ? (
                        `${row.gateway_stream_width}p`
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {row.agent == null ? (
                        <Typography variant="caption" color="text.secondary">
                          미보고
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          color={row.agent_stale ? 'warning.main' : 'text.secondary'}
                        >
                          {formatAge(row.agent_age_sec)}
                          {row.agent_stale ? ' (오래됨)' : ''}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default BrandManagerGatewayStreamMonitorSection;
